import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaDatabase, FaShieldAlt, FaFileAlt, FaChartBar, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

function Home({ onContactClick }) {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [currentTeamIndex, setCurrentTeamIndex] = useState(0);
  const [isAutoPlayActive, setIsAutoPlayActive] = useState(true);

  const teamScenarios = [
    {
      title: "Data Platform Teams",
      challenge: "Resolving the complexity of modernizing legacy systems and siloed infrastructure.",
      description: "Outdated ETL jobs, disjointed APIs, and fragile data handoffs delay critical delivery. Lattice eliminates the wait with auto-published, secure APIs and ready-to-use SDKs — so your data teams can stop patching and start productizing.",
      solution: "Skip custom ETL pipelines. Instantly expose trusted data via secure, reusable APIs."
    },
    {
      title: "DevOps & Integration Teams", 
      challenge: "Delivering secure, scalable API layers without reinventing the wheel.",
      description: "Custom API gateways, identity integration, and compliance frameworks shouldn't be a build-from-scratch project. Lattice ships with OIDC-ready auth, OpenAPI docs, and SDKs — so DevOps can automate and scale with confidence, not duct tape.",
      solution: "End the delays caused by brittle handoffs between infrastructure and application teams."
    },
    {
      title: "Analysts & Dashboard Builders",
      challenge: "Breaking free from engineering bottlenecks and stale data pipelines.",
      description: "Stop waiting for someone to write the API, grant access, or manually build a dashboard. Lattice gives analysts live, authenticated endpoints and everything needed to deploy Power BI dashboards — so insights arrive while they still matter.",
      solution: "Go from schema to live dashboard without submitting tickets or waiting weeks."
    },
    {
      title: "Government, DoD & Regulated Environments",
      challenge: "Meeting compliance, access control, and data-sharing mandates — fast.",
      description: "When data must be secure, auditable, and access-controlled by design, Lattice delivers. With ICAM-ready integration, STIG-aligned architecture, and live API-to-dashboard workflows, it's built for IL5 and beyond — without slowing you down.",
      solution: "Built to reduce complexity around IL5, Zero Trust, ICAM, and auditability."
    }
  ];

  const openVideoModal = () => {
    setIsVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
  };

  //every 15 seconds
  useEffect(() => {
    if (!isAutoPlayActive) return;

    const timer = setInterval(() => {
      setCurrentTeamIndex((prevIndex) => 
        (prevIndex + 1) % teamScenarios.length
      );
    }, 15000);

    return () => clearInterval(timer);
  }, [teamScenarios.length, isAutoPlayActive]);

  const nextTeam = () => {
    setIsAutoPlayActive(false);
    setCurrentTeamIndex((prevIndex) => 
      (prevIndex + 1) % teamScenarios.length
    );
  };

  const prevTeam = () => {
    setIsAutoPlayActive(false);
    setCurrentTeamIndex((prevIndex) => 
      prevIndex === 0 ? teamScenarios.length - 1 : prevIndex - 1
    );
  };

  const goToTeam = (index) => {
    setIsAutoPlayActive(false);
    setCurrentTeamIndex(index);
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content-layout">
          <div className="hero-text">
            <h1>Productize Your Data — FAST</h1>
            <p className="hero-subtitle">
              From raw database to secure API to dashboard — in minutes, not months.
            </p>
            <p className="hero-description">
              Lattice turns your internal data sources into fully packaged, enterprise-ready data products — with built-in security, documentation, and instant analytics.
            </p>
            <div className="cta-buttons">
              <button onClick={onContactClick} className="cta-primary">Learn More</button>
            </div>
          </div>
          <div className="hero-video-section">
            <div className="video-container" onClick={openVideoModal}>
              <video 
                className="platform-demo-video"
                autoPlay 
                loop 
                muted 
                playsInline
                poster="/ilab_fast_data_focus_demo_poster.jpg"
              >
                <source src="/lattice_demo_vid_for_site.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="video-overlay">
                <div className="play-button">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="rgba(0, 0, 0, 0.7)" stroke="white" strokeWidth="2"/>
                    <path d="M10 8l6 4-6 4V8z" fill="white"/>
                  </svg>
                </div>
                <p className="expand-text">Click to expand</p>
              </div>
            </div>
            <p className="video-caption">See how Lattice transforms your data into APIs, SDKs and dashboards</p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="section-header">
          <span className="section-tag">What You Can Do with Lattice</span>
          <h2>Transform your data into enterprise-ready products with built-in security and documentation</h2>
        </div>
        
        <div className="services-grid">
          <div className="service-card">
            <div className="service-icon">
              <FaDatabase size={40} color="#2B9BCA" />
            </div>
            <h3>Publish Your Database as a REST API</h3>
            <p>
              Turn any relational database into a live, documented REST API — no custom coding required.
            </p>
            <div className="service-features">
              <span>Auto-detects tables, views, and fields</span>
              <span>Lets you publish only what you need</span>
              <span>Supports real-time or scheduled refreshes</span>
            </div>
          </div>

          <div className="service-card">
            <div className="service-icon">
              <FaShieldAlt size={40} color="#2B9BCA" />
            </div>
            <h3>Secure with Enterprise Identity</h3>
            <p>
              Connect your API to your enterprise ICAM system (OIDC, SSO, etc.) for secure access control from day one.
            </p>
            <div className="service-features">
              <span>Built for GovCloud, DoD, and federal-grade compliance</span>
              <span>Supports token-based access and scopes</span>
              <span>Ready for Zero Trust environments</span>
            </div>
          </div>

          <div className="service-card">
            <div className="service-icon">
              <FaFileAlt size={40} color="#2B9BCA" />
            </div>
            <h3>Auto‑Generate SDKs & OpenAPI Docs</h3>
            <p>
              Your API is instantly usable by developers and analysts with auto-generated client libraries and comprehensive documentation.
            </p>
            <div className="service-features">
              <span>Get client SDKs in Python, Java, and .NET</span>
              <span>Auto-generated OpenAPI spec for tooling integration</span>
              <span>Customize field-level documentation and usage notes</span>
            </div>
          </div>

          <div className="service-card">
            <div className="service-icon">
              <FaChartBar size={40} color="#2B9BCA" />
            </div>
            <h3>Rapid Dashboard Deployment</h3>
            <p>
              Skip the manual setup. Deploy dashboards for Lattice, PowerBI, Tableau, Springboot, and other rapid app deployments with everything you need to get started.
            </p>
            <div className="service-features">
              <span>Pre-configured templates and connection frameworks</span>
              <span>Users define metrics, KPIs, and data requirements via simple UI</span>
              <span>Deploy with minimal configuration</span>
            </div>
          </div>
        </div>
      </section>

      {/* Perfect For Section - Carousel */}
      <section className="perfect-for-section">
        <div className="section-header">
          <h2>For Teams Who Are <span className="underlined">Done</span> Waiting</h2>
        </div>
        
        <div className="teams-carousel">
          <div className="carousel-container">
            <button className="carousel-nav carousel-prev" onClick={prevTeam} aria-label="Previous team">
              <FaChevronLeft />
            </button>
            
            <div className="carousel-content">
              <div className="team-card-carousel">
                <div className="team-content">
                  <h3>{teamScenarios[currentTeamIndex].title}</h3>
                  <p className="team-challenge">{teamScenarios[currentTeamIndex].challenge}</p>
                  <p className="team-description">{teamScenarios[currentTeamIndex].description}</p>
                  {teamScenarios[currentTeamIndex].title === "Data Platform Teams" && (
                    <div className="team-image-container">
                      <img 
                        src="/DataPlatformTeams.png" 
                        alt="Data Platform Teams architecture diagram"
                        className="team-image"
                      />
                    </div>
                  )}
                  {teamScenarios[currentTeamIndex].title === "DevOps & Integration Teams" && (
                    <div className="team-image-container">
                      <img 
                        src="/DevOpsIntegrationTeams.png" 
                        alt="DevOps & Integration Teams architecture diagram"
                        className="team-image"
                      />
                    </div>
                  )}
                  {teamScenarios[currentTeamIndex].title === "Analysts & Dashboard Builders" && (
                    <div className="team-image-container">
                      <img 
                        src="/AnalystsDashboardBuilders.png" 
                        alt="Analysts & Dashboard Builders architecture diagram"
                        className="team-image"
                      />
                    </div>
                  )}
                  {teamScenarios[currentTeamIndex].title === "Government, DoD & Regulated Environments" && (
                    <div className="team-image-container">
                      <img 
                        src="/GovernmentDoDRegulatedEnvironments.png" 
                        alt="Government, DoD & Regulated Environments architecture diagram"
                        className="team-image"
                      />
                    </div>
                  )}
                  <p className="team-solution">{teamScenarios[currentTeamIndex].solution}</p>
                </div>
              </div>
            </div>
            
            <button className="carousel-nav carousel-next" onClick={nextTeam} aria-label="Next team">
              <FaChevronRight />
            </button>
          </div>
          
          <div className="carousel-dots">
            {teamScenarios.map((_, index) => (
              <button
                key={index}
                className={`carousel-dot ${index === currentTeamIndex ? 'active' : ''}`}
                onClick={() => goToTeam(index)}
                aria-label={`Go to team ${index + 1}`}
              />
            ))}
          </div>
        </div>
        
        <div className="teams-tagline">
          <p className="impact-description">
            Productize your data. Secure it. Share it.<br />
            Lattice — Built for speed. Trusted for scale.
          </p>
        </div>
      </section>

      {/* Why It Matters Section */}
      <section className="why-matters-section">
        <div className="wim-content">
          <div className="wim-text">
            <span className="section-tag">Why It Matters</span>
            <h2>From Oracle to Postgres, Lattice makes it easy to publish, secure, and visualize your data</h2>
            <div className="feature-pillars">
              <div className="pillar">
                <h4>Secure by Design</h4>
                <p>Works with your existing identity systems and compliance requirements</p>
              </div>
              <div className="pillar">
                <h4>Developer-Friendly</h4>
                <p>Auto-generates everything needed for API consumers — no guesswork</p>
              </div>
              <div className="pillar">
                <h4>Analyst-Ready</h4>
                <p>Prebuilt dashboards let analysts explore live data without asking for access</p>
              </div>
              <div className="pillar">
                <h4>No More Data Silos</h4>
                <p>Expose, share, and reuse data across teams and tools — all from a central platform</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="final-cta-section">
        <div className="cta-content">
          <h2>Ready to productize your data?</h2>
          <p>Transform your database into secure APIs and dashboards today</p>
          <button onClick={onContactClick} className="cta-primary large">Get Started</button>
        </div>
      </section>


      {/* Video Modal */}
      {isVideoModalOpen && (
        <div className="video-modal-overlay" onClick={closeVideoModal}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="video-modal-close" onClick={closeVideoModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <video 
              className="video-modal-video"
              autoPlay 
              loop 
              muted 
              playsInline
              controls
            >
              <source src="/lattice_demo_vid_for_site.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
